/**
 * WeaveIn.ai Platform - Emergency Incident List
 * Lists active and recent emergency incidents with severity badges,
 * affected member counts, and quick actions.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Plus,
  MapPin,
  Users,
  Clock,
  CheckCircle,
  Radio,
  Filter,
  ExternalLink,
  Bell,
} from 'lucide-react';
import { emergencyAPI, EmergencyIncident, IncidentStatus, IncidentType, IncidentSeverity } from '@/services/api/emergency';

const SEVERITY_CONFIG: Record<string, { color: string; bg: string }> = {
  advisory: { color: 'text-blue-700', bg: 'bg-blue-100' },
  watch: { color: 'text-yellow-700', bg: 'bg-yellow-100' },
  warning: { color: 'text-orange-700', bg: 'bg-orange-100' },
  emergency: { color: 'text-red-700', bg: 'bg-red-100' },
};

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
  active: { color: 'text-red-600', icon: <Radio className="w-3 h-3 animate-pulse" /> },
  monitoring: { color: 'text-yellow-600', icon: <Clock className="w-3 h-3" /> },
  resolved: { color: 'text-green-600', icon: <CheckCircle className="w-3 h-3" /> },
  closed: { color: 'text-gray-500', icon: <CheckCircle className="w-3 h-3" /> },
};

interface EmergencyIncidentListProps {
  onSelectIncident: (incidentId: number, incidentName?: string) => void;
  onCreateIncident: () => void;
  onViewAlerts?: () => void;
}

export const EmergencyIncidentList: React.FC<EmergencyIncidentListProps> = ({
  onSelectIncident,
  onCreateIncident,
  onViewAlerts,
}) => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newIncident, setNewIncident] = useState({
    incident_name: '',
    incident_type: 'natural_disaster' as IncidentType,
    severity: 'warning' as IncidentSeverity,
    description: '',
    affected_geography: { states: [] as string[], zip_codes: [] as string[] },
  });

  const createMutation = useMutation({
    mutationFn: () => emergencyAPI.createIncident(newIncident),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['emergency-incidents'] });
      setShowCreateForm(false);
      setNewIncident({ incident_name: '', incident_type: 'natural_disaster', severity: 'warning', description: '', affected_geography: { states: [], zip_codes: [] } });
      if (data.id) onSelectIncident(data.id, newIncident.incident_name);
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['emergency-incidents', statusFilter],
    queryFn: () => emergencyAPI.listIncidents(statusFilter as IncidentStatus || undefined),
    refetchInterval: 60000,
  });

  // External alerts count
  const { data: alertsData } = useQuery({
    queryKey: ['external-alerts'],
    queryFn: () => emergencyAPI.getExternalAlerts(),
    refetchInterval: 120000,
  });

  const incidents = data?.incidents || [];
  const alertCount = alertsData?.alerts?.length || 0;

  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="flex items-center gap-2">
            {alertCount > 0 && (
              <button
                onClick={onViewAlerts}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-md"
              >
                <Bell className="w-3.5 h-3.5" />
                {alertCount} External Alert{alertCount !== 1 ? 's' : ''}
              </button>
            )}
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
            >
              <Plus className="w-3.5 h-3.5" />
              New Incident
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mt-3">
          <Filter className="w-3.5 h-3.5 text-gray-400" />
          {['', 'active', 'monitoring', 'resolved'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 text-xs rounded-md ${
                statusFilter === s
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Create Incident Form */}
      {showCreateForm && (
        <div className="mx-6 mt-4 p-4 bg-white border-2 border-red-200 rounded-lg space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Create New Incident</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Incident Name *</label>
              <input
                type="text"
                value={newIncident.incident_name}
                onChange={e => setNewIncident({ ...newIncident, incident_name: e.target.value })}
                placeholder="e.g., Hurricane Milton - Tampa Bay"
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={newIncident.incident_type}
                onChange={e => setNewIncident({ ...newIncident, incident_type: e.target.value as IncidentType })}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="natural_disaster">Natural Disaster</option>
                <option value="public_health">Public Health</option>
                <option value="facility_emergency">Facility Emergency</option>
                <option value="mass_casualty">Mass Casualty</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Severity</label>
              <select
                value={newIncident.severity}
                onChange={e => setNewIncident({ ...newIncident, severity: e.target.value as IncidentSeverity })}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="advisory">Advisory</option>
                <option value="watch">Watch</option>
                <option value="warning">Warning</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Affected States</label>
              <input
                type="text"
                value={(newIncident.affected_geography.states || []).join(', ')}
                onChange={e => setNewIncident({
                  ...newIncident,
                  affected_geography: {
                    ...newIncident.affected_geography,
                    states: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                  },
                })}
                placeholder="e.g., TX, FL, LA"
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              value={newIncident.description}
              onChange={e => setNewIncident({ ...newIncident, description: e.target.value })}
              placeholder="Brief description of the emergency..."
              rows={2}
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => createMutation.mutate()}
              disabled={!newIncident.incident_name || createMutation.isPending}
              className="px-4 py-2 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create & Open Incident'}
            </button>
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-xs text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Active incidents banner */}
      {incidents.filter(i => i.status === 'active').length > 0 && (
        <div className="mx-6 mt-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <Radio className="w-4 h-4 text-red-500 animate-pulse" />
          <span className="text-sm font-medium text-red-700">
            {incidents.filter(i => i.status === 'active').length} active incident(s)
          </span>
        </div>
      )}

      {/* Incident Cards */}
      <div className="p-6 space-y-3">
        {isLoading && (
          <div className="text-center py-12 text-gray-400 text-sm">Loading incidents...</div>
        )}

        {!isLoading && incidents.length === 0 && (
          <div className="text-center py-12">
            <AlertTriangle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No emergency incidents</p>
          </div>
        )}

        {incidents.map(incident => {
          const sevCfg = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.watch;
          const staCfg = STATUS_CONFIG[incident.status] || STATUS_CONFIG.active;
          const geo = incident.affected_geography || {};
          const geoSummary = [
            ...(geo.states || []),
            ...(geo.counties || []).slice(0, 2),
          ].join(', ');

          return (
            <div
              key={incident.id}
              onClick={() => onSelectIncident(incident.id, incident.incident_name)}
              className={`border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow ${
                incident.status === 'active' ? 'border-red-200' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`flex items-center gap-1 text-xs font-medium ${staCfg.color}`}>
                      {staCfg.icon}
                      {incident.status}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sevCfg.bg} ${sevCfg.color}`}>
                      {incident.severity.toUpperCase()}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      {incident.incident_type.replace('_', ' ')}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">{incident.incident_name}</h3>
                  {incident.description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{incident.description}</p>
                  )}
                </div>
                <ExternalLink className="w-4 h-4 text-gray-300" />
              </div>

              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {incident.affected_member_count} members
                </span>
                {geoSummary && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {geoSummary}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {incident.activated_at ? new Date(incident.activated_at).toLocaleDateString() : 'N/A'}
                </span>
                {incident.external_alert_source && (
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">
                    {incident.external_alert_source.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmergencyIncidentList;
